// src/frontend/js/modern/core/storage/display-store.js
//
// Dedicated display-data layer backed by IndexedDB (via ResilientStorage).
//
// Product display data (name, image HTML, price HTML, URLs, nonces) is bulky.
// Keeping it OUT of the main persistent store means:
//   1. Store mutations serialize ~10× less data (only IDs + flags)
//   2. Display data survives localStorage eviction (IDB is more durable)
//   3. Cross-tab messages carry only IDs, not HTML payloads
//
// Read path : always from the synchronous RAM Map (pre-loaded at boot).
// Write path: RAM Map updated synchronously + IDB written async (fire-and-forget).
//
// Namespaced by module ('wishlist' | 'waitlist') to avoid product-ID collisions
// when the same product exists in both lists.

import { resilientStorage } from './resilient-storage.js';

const KEY_PREFIX = '_display_';
const MAX_ENTRIES_PER_MODULE = 500;

/**
 * Fields that are PRODUCT-LEVEL (same regardless of which list the product
 * is in). Only these are stored in the DisplayStore.
 * List-specific fields (item_id, wishlist_id, remove_url, remove_nonce,
 * list_name) remain on the lightweight items in the persistent store.
 */
const SHARED_FIELDS = new Set(['name', 'image', 'price', 'url']);

/**
 * Extract only shared (product-level) fields from a full display object.
 */
function extractShared(displayData) {
	if (!displayData) return null;
	const shared = {};
	for (const field of SHARED_FIELDS) {
		if (displayData[field] !== undefined) {
			shared[field] = displayData[field];
		}
	}
	return Object.keys(shared).length > 0 ? shared : null;
}

class DisplayStore {
	constructor() {
		// moduleName → Map<productId, displayObject>
		this._cache = new Map();
		this._recovered = new Set();
	}

	_key(moduleName, productId) {
		return `${KEY_PREFIX}${moduleName}_${productId}`;
	}

	_moduleEntries(moduleName) {
		if (!this._cache.has(moduleName)) {
			this._cache.set(moduleName, new Map());
		}
		return this._cache.get(moduleName);
	}

	/**
	 * Store SHARED display data for a product within a module.
	 * Only product-level fields (name/image/price/url) are stored.
	 * List-specific fields (remove_url, nonce, item_id) live on the item.
	 * RAM Map is updated synchronously; IDB write is fire-and-forget.
	 */
	set(moduleName, productId, data) {
		const shared = extractShared(data);
		if (!shared) return;

		const entries = this._moduleEntries(moduleName);

		// Merge: don't overwrite existing shared fields with undefined.
		const existing = entries.get(String(productId)) || {};
		entries.set(String(productId), { ...existing, ...shared });

		if (entries.size > MAX_ENTRIES_PER_MODULE) {
			const firstKey = entries.keys().next().value;
			entries.delete(firstKey);
			resilientStorage.remove(this._key(moduleName, firstKey));
		}

		resilientStorage.set(
			this._key(moduleName, productId),
			entries.get(String(productId))
		);
	}

	/**
	 * Synchronous read from RAM cache.
	 * Returns null if not found.
	 */
	get(moduleName, productId) {
		const entries = this._moduleEntries(moduleName);
		return entries.get(String(productId)) || null;
	}

	/**
	 * Batch-write display data extracted from sync/hydration items.
	 * Items format: [{ product_id, display: {...} }, ...]
	 */
	bulkSet(moduleName, items) {
		if (!Array.isArray(items)) return;
		for (const item of items) {
			if (item?.display && item.product_id !== undefined) {
				this.set(moduleName, item.product_id, item.display);
			}
		}
	}

	/**
	 * Pre-load all cached display entries for a module from IDB into RAM.
	 * Must complete BEFORE any rendering that depends on display data.
	 */
	async recover(moduleName) {
		if (this._recovered.has(moduleName)) return;
		this._recovered.add(moduleName);

		try {
			const prefix = `${KEY_PREFIX}${moduleName}_`;
			const allKeys = await resilientStorage.keys();

			for (const fullKey of allKeys) {
				if (typeof fullKey !== 'string' || !fullKey.startsWith(prefix)) continue;
				const productId = fullKey.slice(prefix.length);
				const data = await resilientStorage.get(fullKey);
				if (data && (data.name || data.image)) {
					this._moduleEntries(moduleName).set(String(productId), data);
				}
			}
		} catch (error) {
			console.warn(`[DisplayStore] Recovery failed for ${moduleName}:`, error);
		}
	}

	/**
	 * Clear all entries for a module (e.g., after account switch).
	 */
	clear(moduleName) {
		const entries = this._moduleEntries(moduleName);
		for (const [productId] of entries) {
			resilientStorage.remove(this._key(moduleName, productId));
		}
		entries.clear();
	}

	/**
	 * Remove a single product's shared display entry for a module.
	 */
	remove(moduleName, productId) {
		const entries = this._moduleEntries(moduleName);
		if (!entries.delete(String(productId))) return;
		resilientStorage.remove(this._key(moduleName, productId));
	}
}

export const displayStore = new DisplayStore();
export default displayStore;
